import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import makeStyles from "@mui/styles/makeStyles";
import { green } from "@mui/material/colors";

import {
	Dialog,
	DialogContent,
	DialogTitle,
	Button,
	DialogActions,
	CircularProgress,
	TextField,
	MenuItem,
	Switch,
	FormControlLabel,
	Typography,
	Box,
	IconButton,
	InputAdornment,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import MetaCoexSignup from "../MetaCoexSignup";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},

	multFieldLine: {
		display: "flex",
		"& > *:not(:last-child)": {
			marginRight: theme.spacing(1),
		},
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
}));

const SessionSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Required"),
});

const randomWidgetKey = () =>
	`wc${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;

const WhatsAppModal = ({ open, onClose, whatsAppId }) => {
	const classes = useStyles();
	const initialState = {
		name: "",
		greetingMessage: "",
		isDefault: false,
		status: "",
		session: "",
		providerType: "whatsapp_web",
		connectionMode: "manual",
		metaAccessToken: "",
		phoneNumberId: "",
		instagramRecipientId: "",
		widgetKey: "",
		widgetTitle: "Atendimento",
		primaryColor: "#0f766e",
		embedCode: "",
	};
	const [whatsApp, setWhatsApp] = useState(initialState);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [coexWhatsAppId, setCoexWhatsAppId] = useState(null);
	const [coexModalOpen, setCoexModalOpen] = useState(false);

	const loadEmbedCode = async id => {
		if (!id) return;
		try {
			const { data } = await api.get(`/whatsapp/${id}/website-embed-code`);
			setWhatsApp(prev => ({
				...prev,
				widgetKey: data.widgetKey || prev.widgetKey,
				primaryColor: data.primaryColor || prev.primaryColor,
				widgetTitle: data.title || prev.widgetTitle,
				embedCode: data.embedCode || "",
			}));
		} catch {
			// conexão ainda não salva como website_embed
		}
	};

	useEffect(() => {
		const fetchSession = async () => {
			if (!whatsAppId) return;

			try {
				const { data } = await api.get(`whatsapp/${whatsAppId}`);
				let providerType = "whatsapp_web";
				let connectionMode = "manual";
				let metaAccessToken = "";
				let phoneNumberId = "";
				let instagramRecipientId = "";
				let widgetKey = "";
				let widgetTitle = "Atendimento";
				let primaryColor = "#0f766e";

				try {
					if (data?.session) {
						const parsed = JSON.parse(data.session);
						const provider = parsed?.meta?.provider;
						if (provider === "whatsapp_cloud") {
							providerType = "whatsapp_cloud";
							connectionMode = parsed?.meta?.connectionMode || "manual";
							metaAccessToken = parsed?.meta?.accessToken || "";
							phoneNumberId = parsed?.meta?.phoneNumberId || "";
						}
						if (provider === "instagram_dm") {
							providerType = "instagram_dm";
							metaAccessToken = parsed?.meta?.accessToken || "";
							instagramRecipientId = parsed?.meta?.instagramRecipientId || "";
						}
						if (provider === "website_embed") {
							providerType = "website_embed";
							widgetKey = parsed?.meta?.widgetKey || "";
							widgetTitle = parsed?.meta?.title || data.name || "Atendimento";
							primaryColor = parsed?.meta?.primaryColor || "#0f766e";
						}
					}
				} catch {
					// sessão wbot (JSON de autenticação) ou string inválida
				}

				setWhatsApp({
					...data,
					providerType,
					connectionMode,
					metaAccessToken,
					phoneNumberId,
					instagramRecipientId,
					widgetKey,
					widgetTitle,
					primaryColor,
					embedCode: "",
				});

				const whatsQueueIds = data.queues?.map(queue => queue.id);
				setSelectedQueueIds(whatsQueueIds);

				if (providerType === "website_embed") {
					await loadEmbedCode(whatsAppId);
				}
			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
	}, [whatsAppId]);

	const handleSaveWhatsApp = async values => {
		let session = values.session || "";

		if (values.providerType === "website_embed") {
			session = JSON.stringify({
				meta: {
					provider: "website_embed",
					widgetKey: values.widgetKey || randomWidgetKey(),
					title: values.widgetTitle || values.name || "Atendimento",
					primaryColor: values.primaryColor || "#0f766e",
				},
			});
		} else if (values.providerType !== "whatsapp_web") {
			const metaProvider =
				values.providerType === "whatsapp_cloud"
					? "whatsapp_cloud"
					: "instagram_dm";

			session = JSON.stringify({
				meta: {
					provider: metaProvider,
					connectionMode:
						values.providerType === "whatsapp_cloud"
							? values.connectionMode || "manual"
							: undefined,
					accessToken: values.metaAccessToken || "",
					phoneNumberId: values.phoneNumberId || undefined,
					instagramRecipientId: values.instagramRecipientId || undefined,
				},
			});
		}

		const whatsappData = {
			name: values.name,
			status: values.status || undefined,
			session,
			greetingMessage: values.greetingMessage,
			isDefault: values.isDefault,
			queueIds: selectedQueueIds,
		};

		try {
			let savedWhatsAppId = whatsAppId;

			if (whatsAppId) {
				await api.put(`/whatsapp/${whatsAppId}`, whatsappData);
			} else {
				const { data } = await api.post("/whatsapp", whatsappData);
				savedWhatsAppId = data?.id;
			}

			toast.success(i18n.t("whatsappModal.success"));

			if (values.providerType === "website_embed" && savedWhatsAppId) {
				await loadEmbedCode(savedWhatsAppId);
				toast.info("Copie o código HTML e cole no site/landing page.");
				return;
			}

			if (
				values.providerType === "whatsapp_cloud" &&
				values.connectionMode === "coex" &&
				savedWhatsAppId
			) {
				setCoexWhatsAppId(savedWhatsAppId);
				setCoexModalOpen(true);
				return;
			}

			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	const handleClose = () => {
		onClose();
		setWhatsApp(initialState);
		setCoexWhatsAppId(null);
		setCoexModalOpen(false);
	};

	const handleCoexConnected = () => {
		setCoexModalOpen(false);
		setCoexWhatsAppId(null);
		handleClose();
	};

	const copyEmbed = async code => {
		try {
			await navigator.clipboard.writeText(code);
			toast.success("Código copiado.");
		} catch {
			toastError({ message: "Não foi possível copiar." });
		}
	};

	return (
		<div className={classes.root}>
			<MetaCoexSignup
				open={coexModalOpen}
				whatsAppId={coexWhatsAppId || whatsAppId}
				autoLaunch
				showInlineButton={false}
				onConnected={handleCoexConnected}
				onClose={() => setCoexModalOpen(false)}
			/>
			<Dialog
				open={open && !coexModalOpen}
				onClose={handleClose}
				maxWidth="sm"
				fullWidth
				scroll="paper"
			>
				<DialogTitle>
					{whatsAppId
						? i18n.t("whatsappModal.title.edit")
						: i18n.t("whatsappModal.title.add")}
				</DialogTitle>
				<Formik
					initialValues={whatsApp}
					enableReinitialize={true}
					validationSchema={SessionSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveWhatsApp(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, touched, errors, isSubmitting, setFieldValue }) => (
						<Form>
							<DialogContent dividers>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("whatsappModal.form.name")}
										autoFocus
										name="name"
										error={touched.name && Boolean(errors.name)}
										helperText={touched.name && errors.name}
										variant="outlined"
										margin="dense"
										className={classes.textField}
									/>
									<FormControlLabel
										control={
											<Field
												as={Switch}
												color="primary"
												name="isDefault"
												checked={values.isDefault}
											/>
										}
										label={i18n.t("whatsappModal.form.default")}
									/>
								</div>
								<div>
									<Field
										as={TextField}
										label={i18n.t("queueModal.form.greetingMessage")}
										type="greetingMessage"
										multiline
										rows={5}
										fullWidth
										name="greetingMessage"
										error={
											touched.greetingMessage && Boolean(errors.greetingMessage)
										}
										helperText={
											touched.greetingMessage && errors.greetingMessage
										}
										variant="outlined"
										margin="dense"
									/>
								</div>

								<div>
									<Field
										as={TextField}
										select
										label="Integração"
										name="providerType"
										variant="outlined"
										margin="dense"
										fullWidth
										onChange={e => {
											const next = e.target.value;
											setFieldValue("providerType", next);
											if (next === "website_embed" && !values.widgetKey) {
												setFieldValue("widgetKey", randomWidgetKey());
											}
										}}
									>
										<MenuItem value="whatsapp_web">WhatsApp Web</MenuItem>
										<MenuItem value="whatsapp_cloud">WhatsApp Cloud (Meta)</MenuItem>
										<MenuItem value="instagram_dm">Instagram DM (Meta)</MenuItem>
										<MenuItem value="website_embed">Chat Site (sites / landing pages)</MenuItem>
									</Field>
								</div>

								{values.providerType === "whatsapp_cloud" && (
									<>
										<div>
											<Field
												as={TextField}
												select
												label="Modo de conexão"
												name="connectionMode"
												variant="outlined"
												margin="dense"
												fullWidth
											>
												<MenuItem value="manual">Manual (Token + Phone Number ID)</MenuItem>
												<MenuItem value="coex">Oficial Coex (QR WhatsApp Business)</MenuItem>
											</Field>
										</div>
										{values.connectionMode === "manual" && (
											<>
												<div>
													<Field
														as={TextField}
														label="Meta Access Token"
														name="metaAccessToken"
														variant="outlined"
														margin="dense"
														fullWidth
														multiline
														rows={3}
													/>
												</div>
												<div>
													<Field
														as={TextField}
														label="Phone Number ID"
														name="phoneNumberId"
														variant="outlined"
														margin="dense"
														fullWidth
													/>
												</div>
											</>
										)}
										{values.connectionMode === "coex" && (whatsAppId || coexWhatsAppId) && (
											<MetaCoexSignup
												whatsAppId={whatsAppId || coexWhatsAppId}
												open
												onConnected={handleCoexConnected}
											/>
										)}
										{values.connectionMode === "coex" && !whatsAppId && !coexWhatsAppId && (
											<Typography variant="body2" color="textSecondary">
												Após salvar, a janela de leitura do QR será aberta automaticamente.
											</Typography>
										)}
									</>
								)}

								{values.providerType === "instagram_dm" && (
									<>
										<div>
											<Field
												as={TextField}
												label="Meta Access Token"
												name="metaAccessToken"
												variant="outlined"
												margin="dense"
												fullWidth
												multiline
												rows={3}
											/>
										</div>
										<div>
											<Field
												as={TextField}
												label="Instagram Recipient ID (page/id)"
												name="instagramRecipientId"
												variant="outlined"
												margin="dense"
												fullWidth
											/>
										</div>
									</>
								)}

								{values.providerType === "website_embed" && (
									<>
										<Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
											Gera um widget de chat no rodapé. O código deve ser usado em
											páginas <strong>HTTPS</strong>. O visitante informa nome,
											telefone e e-mail antes de iniciar o atendimento (tickets
											com canal <strong>Chat Site</strong>).
										</Typography>
										<div>
											<Field
												as={TextField}
												label="Título do widget"
												name="widgetTitle"
												variant="outlined"
												margin="dense"
												fullWidth
											/>
										</div>
										<div>
											<Field
												as={TextField}
												label="Cor principal"
												name="primaryColor"
												variant="outlined"
												margin="dense"
												fullWidth
												placeholder="#0f766e"
											/>
										</div>
										{(values.embedCode || whatsApp.embedCode) && (
											<Box sx={{ mt: 1 }}>
												<TextField
													label="Código para inserir no site"
													value={values.embedCode || whatsApp.embedCode}
													fullWidth
													multiline
													minRows={3}
													variant="outlined"
													margin="dense"
													InputProps={{
														readOnly: true,
														endAdornment: (
															<InputAdornment position="end">
																<IconButton
																	onClick={() =>
																		copyEmbed(values.embedCode || whatsApp.embedCode)
																	}
																>
																	<ContentCopyIcon fontSize="small" />
																</IconButton>
															</InputAdornment>
														),
													}}
												/>
											</Box>
										)}
									</>
								)}

								<QueueSelect
									selectedQueueIds={selectedQueueIds}
									onChange={selectedIds => setSelectedQueueIds(selectedIds)}
								/>
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("whatsappModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{whatsAppId
										? i18n.t("whatsappModal.buttons.okEdit")
										: i18n.t("whatsappModal.buttons.okAdd")}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default React.memo(WhatsAppModal);
